import { DEFAULT_ROOM_FLOOR_COLOR } from './floorPlanDesignerRooms.js';
import { DEFAULT_WALL_COLOR } from './floorPlanDesignerState.js';
import { getTextureCatalogEntry } from './floorPlanTextureCatalog.js';

/** @typedef {{ materialMode: 'color' | 'texture'; color: string; textureId: string | null }} FloorPlanMaterialConfig */

export const DEFAULT_WALL_TEXTURE_ID = 'wall_inside_texture_2';
export const DEFAULT_FLOOR_TEXTURE_ID = 'floor_texture_4';

export const DEFAULT_WALL_TEXTURE_MATERIAL = {
  materialMode: 'texture',
  textureId: DEFAULT_WALL_TEXTURE_ID,
  color: '#FFFFFF',
};

export const DEFAULT_WALL_OUTSIDE_TEXTURE_MATERIAL = {
  materialMode: 'texture',
  textureId: 'wall-outside-default',
  color: '#FFFFFF',
};

export const DEFAULT_FLOOR_TEXTURE_MATERIAL = {
  materialMode: 'texture',
  textureId: DEFAULT_FLOOR_TEXTURE_ID,
  color: '#FFFFFF',
};

export const DEFAULT_WALL_COLOR_MATERIAL = {
  materialMode: 'color',
  textureId: null,
  color: '#3F3F46',
};

export const DEFAULT_FLOOR_COLOR_MATERIAL = {
  materialMode: 'color',
  textureId: null,
  color: '#FFFFFF',
};

/** @param {string} raw */
export function normalizeHexColor(raw) {
  const s = typeof raw === 'string' ? raw.trim() : '';
  const m = /^#?([0-9A-Fa-f]{6})$/.exec(s);
  if (!m) return '';
  return `#${m[1].toUpperCase()}`;
}

/**
 * @param {Partial<FloorPlanMaterialConfig> | null | undefined} material
 * @param {string} [fallbackColor]
 * @returns {FloorPlanMaterialConfig}
 */
export function normalizeMaterialConfig(material, fallbackColor = DEFAULT_WALL_COLOR) {
  const color = normalizeHexColor(material?.color) || normalizeHexColor(fallbackColor) || DEFAULT_WALL_COLOR;
  const mode = material?.materialMode === 'texture' ? 'texture' : 'color';
  const textureId =
    mode === 'texture' && typeof material?.textureId === 'string' ? material.textureId : null;
  const validTexture = textureId && getTextureCatalogEntry(textureId) ? textureId : null;
  return {
    materialMode: validTexture ? 'texture' : 'color',
    color,
    textureId: validTexture,
  };
}

/**
 * @param {{ material?: Partial<FloorPlanMaterialConfig>; insideMaterial?: Partial<FloorPlanMaterialConfig>; outsideMaterial?: Partial<FloorPlanMaterialConfig> } | null | undefined} wall
 * @param {'inside' | 'outside'} side
 */
export function wallHasExplicitMaterial(wall, side = 'inside') {
  if (side === 'inside') {
    return !!(wall?.insideMaterial ?? wall?.material);
  }
  return wall?.outsideMaterial != null;
}

/**
 * @param {{ floorMaterial?: Partial<FloorPlanMaterialConfig> } | null | undefined} roomMeta
 */
export function roomHasExplicitFloorMaterial(roomMeta) {
  return roomMeta?.floorMaterial != null;
}

/**
 * @param {{ color?: string; material?: Partial<FloorPlanMaterialConfig>; insideMaterial?: Partial<FloorPlanMaterialConfig>; outsideMaterial?: Partial<FloorPlanMaterialConfig> } | null | undefined} wall
 * @param {'inside' | 'outside'} [side]
 * @returns {FloorPlanMaterialConfig}
 */
export function getWallMaterialConfig(wall, side = 'inside') {
  const fallbackColor = wall?.color || DEFAULT_WALL_COLOR;
  const sideMaterial = side === 'inside' ? wall?.insideMaterial : wall?.outsideMaterial;

  if (sideMaterial) {
    return normalizeMaterialConfig(sideMaterial, fallbackColor);
  }

  if (side === 'inside' && wall?.material) {
    return normalizeMaterialConfig(wall.material, fallbackColor);
  }

  if (!wallHasExplicitMaterial(wall, side)) {
    const defaultMaterial =
      side === 'outside' ? DEFAULT_WALL_OUTSIDE_TEXTURE_MATERIAL : DEFAULT_WALL_TEXTURE_MATERIAL;
    return normalizeMaterialConfig(defaultMaterial, fallbackColor);
  }

  return normalizeMaterialConfig(DEFAULT_WALL_COLOR_MATERIAL, fallbackColor);
}

/**
 * @param {{ floorColor?: string; floorMaterial?: Partial<FloorPlanMaterialConfig> } | null | undefined} roomMeta
 * @returns {FloorPlanMaterialConfig}
 */
export function getRoomFloorMaterialConfig(roomMeta) {
  if (roomHasExplicitFloorMaterial(roomMeta)) {
    return normalizeMaterialConfig(
      roomMeta.floorMaterial,
      roomMeta.floorColor || DEFAULT_ROOM_FLOOR_COLOR,
    );
  }

  if (roomMeta?.floorColor) {
    return normalizeMaterialConfig(
      {
        materialMode: 'color',
        color: roomMeta.floorColor,
        textureId: null,
      },
      roomMeta.floorColor,
    );
  }

  return normalizeMaterialConfig(DEFAULT_FLOOR_TEXTURE_MATERIAL, DEFAULT_ROOM_FLOOR_COLOR);
}

/**
 * @param {FloorPlanMaterialConfig} material
 * @returns {string}
 */
export function getMaterialDisplayColor(material) {
  return normalizeMaterialConfig(material).color;
}
